import { apiClient, unwrap } from './client';
import type { ApiResponse } from '@/types/api';

/**
 * DTO completo de restricciones del Profile.
 *
 * Mapeo numerico de enums (mismo orden que el backend Domain.Enums):
 *   ConfigureLocale: 0=UserChoice, 1=LocaleConfigDisabled
 *   PrintingPolicy: 0=Allowed, 1=Disabled
 *   CameraAccess: 0=UserChoice, 1=Disabled, 2=Enforced
 *   LocationMode: 0=HighAccuracy, 1=SensorsOnly, 2=BatterySaving, 3=Off
 *   DefaultPermissionPolicy: 0=Prompt, 1=Grant, 2=Deny
 *   PlayStoreMode: 0=Whitelist, 1=Blacklist
 *   AirplaneModeState: 0=Unspecified, 1=UserChoice, 2=Disabled
 *   SystemUpdateType: 0=Automatic, 1=Windowed, 2=Postpone, 3=Unspecified
 *   WifiSecurity: 0=None, 1=Wep, 2=WpaPsk, 3=WpaEap
 */
export interface ProfileRestrictionsDto {
  // Comunes
  shareLocationDisabled: boolean;
  configureLocale: number;
  outgoingBeamDisabled: boolean;
  printingPolicy: number;
  screenCaptureDisabled: boolean;
  cameraAccess: number;
  debuggingFeaturesAllowed: boolean;
  credentialsConfigDisabled: boolean;

  // Ubicacion
  locationMode: number;

  // Apps
  defaultPermissionPolicy: number;
  installAppsDisabled: boolean;
  uninstallAppsDisabled: boolean;
  playStoreMode: number;
  appAutoUpdatePolicy: string;

  // Dispositivo
  addUserDisabled: boolean;
  adjustVolumeDisabled: boolean;
  airplaneModeState: number;
  autoTimeRequired: boolean;
  backupServiceEnabled: boolean;
  bluetoothDisabled: boolean;
  bluetoothConfigDisabled: boolean;
  cellBroadcastsConfigDisabled: boolean;
  dataRoamingDisabled: boolean;
  factoryResetDisabled: boolean;
  funDisabled: boolean;
  mobileNetworksConfigDisabled: boolean;
  mountPhysicalMediaDisabled: boolean;
  networkResetDisabled: boolean;
  outgoingCallsDisabled: boolean;
  safeBootDisabled: boolean;
  smsDisabled: boolean;
  tetheringConfigDisabled: boolean;
  installUnknownSourcesAllowed: boolean;
  unmuteMicrophoneDisabled: boolean;
  usbFileTransferDisabled: boolean;
  userSwitchDisabled: boolean;
  wifiConfigDisabled: boolean;
  modifyAccountsDisabled: boolean;

  // Password
  passwordQuality: string;
  passwordMinimumLength: number;
  maximumTimeToLockMs: number;
  expectedPin: string;

  // FRP
  frpEnabled: boolean;
  frpAccountsCsv?: string | null;

  // WiFi
  wifiEnabled: boolean;
  wifiSsid?: string | null;
  wifiSecurity: number;
  wifiPassphrase?: string | null;  // GET: "***" si seteada, null si no. PUT: ver doc backend.
  wifiHiddenSsid: boolean;
  wifiAutoConnect: boolean;

  // System Update
  systemUpdateType: number;
  systemUpdateStartMinutes?: number | null;
  systemUpdateEndMinutes?: number | null;

  // Reporting
  reportDeviceSettings: boolean;
  reportSoftwareInfo: boolean;
  reportNetworkInfo: boolean;
  reportApplicationReports: boolean;
  reportPowerManagementEvents: boolean;
  reportHardwareStatus: boolean;
  reportMemoryInfo: boolean;
  reportDisplayInfo: boolean;
  reportSystemProperties: boolean;
}

export const profileRestrictionsApi = {
  get(profileId: number): Promise<ProfileRestrictionsDto> {
    return unwrap(
      apiClient.get<ApiResponse<ProfileRestrictionsDto>>(`/profiles/${profileId}/restrictions`),
    );
  },
  update(profileId: number, dto: ProfileRestrictionsDto): Promise<ProfileRestrictionsDto> {
    return unwrap(
      apiClient.put<ApiResponse<ProfileRestrictionsDto>>(
        `/profiles/${profileId}/restrictions`,
        dto,
      ),
    );
  },
};
